// Client-side encryption for PII fields
// Based on PII-ENCRYPTION-ARCHITECTURE.md and privacy-preserving-analytics-spec.md

/**
 * Environment flag to enable/disable encryption
 * - Development: VITE_ENABLE_ENCRYPTION=false (plaintext for debugging)
 * - Production: VITE_ENABLE_ENCRYPTION=true (encrypted for privacy)
 */
const ENCRYPTION_ENABLED = import.meta.env.VITE_ENABLE_ENCRYPTION === 'true';

/**
 * Checks if encryption should be applied
 * Returns false in development mode for easier debugging
 */
export function shouldEncrypt(): boolean {
  return ENCRYPTION_ENABLED;
}

interface EncryptionKey {
  key: CryptoKey;
  salt: Uint8Array;
}

/**
 * ClientEncryption: Handles client-side encryption/decryption of PII fields
 *
 * Security Architecture:
 * - PBKDF2-SHA256 key derivation (100,000 iterations)
 * - AES-256-GCM encryption
 * - 16-byte salt per user (stored separately from key)
 * - 12-byte IV per encryption (stored with ciphertext)
 * - Keys exist only in memory during session
 */
export class ClientEncryption {
  private static encoder = new TextEncoder();
  private static decoder = new TextDecoder();

  /**
   * Derives an encryption key from the user's password using PBKDF2
   *
   * @param password - User's password (master key)
   * @param salt - Optional salt (generates new 16-byte salt if not provided)
   * @returns EncryptionKey with CryptoKey and salt
   */
  static async deriveKeyFromPassword(
    password: string,
    salt?: Uint8Array
  ): Promise<EncryptionKey> {
    // Generate or use provided salt (16 bytes)
    const cryptoSalt = salt || crypto.getRandomValues(new Uint8Array(16));

    // Import password as key material
    const passwordKey = await crypto.subtle.importKey(
      'raw',
      this.encoder.encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveBits', 'deriveKey']
    );

    // Derive AES-256 key from password using PBKDF2
    // 100,000 iterations = industry standard for PBKDF2
    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: cryptoSalt as BufferSource,
        iterations: 100000,
        hash: 'SHA-256'
      },
      passwordKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );

    return { key, salt: cryptoSalt };
  }

  /**
   * Encrypts a string value using AES-256-GCM
   *
   * @param plaintext - Data to encrypt
   * @param key - CryptoKey for encryption
   * @returns Base64-encoded string (IV || ciphertext || auth tag)
   */
  static async encrypt(
    plaintext: string,
    key: CryptoKey
  ): Promise<string> {
    // Generate unique IV for this encryption (12 bytes for GCM)
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Encrypt with AES-GCM (includes authentication tag)
    const encryptedData = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      this.encoder.encode(plaintext)
    );

    // Combine IV + ciphertext for storage
    const combined = new Uint8Array(iv.length + encryptedData.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encryptedData), iv.length);

    // Return as base64 for easy storage
    return btoa(String.fromCharCode(...combined));
  }

  /**
   * Decrypts a string value using AES-256-GCM
   *
   * @param encryptedBase64 - Base64-encoded encrypted data
   * @param key - CryptoKey for decryption
   * @returns Decrypted plaintext string
   * @throws Error if decryption fails (wrong key, corrupted data, etc.)
   */
  static async decrypt(
    encryptedBase64: string,
    key: CryptoKey
  ): Promise<string> {
    // Decode from base64
    const combined = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));

    // Extract IV (first 12 bytes) and ciphertext (rest)
    const iv = combined.slice(0, 12);
    const encryptedData = combined.slice(12);

    // Decrypt with AES-GCM (verifies authentication tag)
    const decryptedData = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encryptedData
    );

    return this.decoder.decode(decryptedData);
  }

  /**
   * Encrypts an object with specified fields encrypted
   * Handles both string and array values
   *
   * @param obj - Object containing data
   * @param fieldsToEncrypt - Array of field names to encrypt
   * @param key - CryptoKey for encryption
   * @returns Object with encrypted fields
   */
  static async encryptFields<T extends Record<string, unknown>>(
    obj: T,
    fieldsToEncrypt: string[],
    key: CryptoKey
  ): Promise<T> {
    const result: Record<string, unknown> = { ...obj };

    for (const field of fieldsToEncrypt) {
      const value = result[field];
      if (typeof value === 'string' && value) {
        result[field] = await this.encrypt(value, key);
      } else if (Array.isArray(value)) {
        // Handle array fields (like person-list for partner names)
        result[field] = await Promise.all(
          value.map(async (item: unknown) => {
            if (typeof item === 'string') {
              return await this.encrypt(item, key);
            }
            return item;
          })
        );
      }
    }

    return result as T;
  }

  /**
   * Decrypts an object with specified fields decrypted
   * Handles both string and array values
   *
   * @param obj - Object containing encrypted data
   * @param fieldsToDecrypt - Array of field names to decrypt
   * @param key - CryptoKey for decryption
   * @returns Object with decrypted fields
   */
  static async decryptFields<T extends Record<string, unknown>>(
    obj: T,
    fieldsToDecrypt: string[],
    key: CryptoKey
  ): Promise<T> {
    const result: Record<string, unknown> = { ...obj };

    for (const field of fieldsToDecrypt) {
      const value = result[field];
      if (typeof value === 'string' && value) {
        try {
          result[field] = await this.decrypt(value, key);
        } catch (error) {
          console.error(`Failed to decrypt field ${field}:`, error);
          // Field remains encrypted if decryption fails
        }
      } else if (Array.isArray(value)) {
        // Handle array fields
        result[field] = await Promise.all(
          value.map(async (item: unknown) => {
            if (typeof item === 'string') {
              try {
                return await this.decrypt(item, key);
              } catch {
                return item; // Return as-is if decryption fails
              }
            }
            return item;
          })
        );
      }
    }

    return result as T;
  }

  /**
   * Stores the salt in localStorage (NOT the key!)
   * Salt is safe to store - it's used with password to derive key
   *
   * @param userId - User ID for salt storage key
   * @param salt - Salt bytes to store
   */
  static storeSalt(userId: string, salt: Uint8Array): void {
    const saltBase64 = btoa(String.fromCharCode(...salt));
    localStorage.setItem(`rc_salt_${userId}`, saltBase64);
  }

  /**
   * Retrieves the stored salt for a user
   *
   * @param userId - User ID to retrieve salt for
   * @returns Salt bytes or null if not found
   */
  static getSalt(userId: string): Uint8Array | null {
    const saltBase64 = localStorage.getItem(`rc_salt_${userId}`);
    if (!saltBase64) return null;

    return Uint8Array.from(atob(saltBase64), c => c.charCodeAt(0));
  }

  /**
   * Generates a random 256-bit AES-GCM key for shared use (e.g., joint responses).
   * Returns both the CryptoKey and its base64-encoded raw bytes for storage.
   */
  static async generateRelationshipKey(): Promise<{ key: CryptoKey; rawBase64: string }> {
    const key = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
    const raw = await crypto.subtle.exportKey('raw', key);
    const rawBase64 = btoa(String.fromCharCode(...new Uint8Array(raw)));
    return { key, rawBase64 };
  }

  /**
   * Imports a relationship key from its base64-encoded raw bytes.
   * Used to load the shared joint-response key from Firestore.
   */
  static async importRelationshipKey(rawBase64: string): Promise<CryptoKey> {
    const raw = Uint8Array.from(atob(rawBase64), c => c.charCodeAt(0));
    return crypto.subtle.importKey(
      'raw',
      raw,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }
}

/**
 * DEPRECATED: All question responses are now encrypted when shouldEncrypt() is true.
 * This list is kept for reference only, documenting which fields were previously
 * considered PII before the decision to encrypt all responses.
 *
 * Per privacy-preserving-analytics-spec.md Data Classification:
 * "Question responses (PII and non-PII fields) - YES encrypted"
 *
 * Previously encrypted fields (for historical reference):
 * - Entire categories: Substance Use, Sexual Health, Mental Health
 * - Critical individual questions: Self-harm/suicide question
 * - Emergency contacts and medical POA
 */
export const PII_FIELDS: Record<string, boolean> = {
  // ========== INDIVIDUAL WORK QUESTIONS ==========

  // Substance Use & Addictive Behaviors (ALL questions in category)
  'substance.1': true,  // Relationship with alcohol
  'substance.2': true,  // Relationship with recreational drugs
  'substance.3': true,  // Compulsive behaviors
  'substance.4': true,  // Circumstances for partner concern
  'substance.5': true,  // Role in social life and stress management

  // Crisis Triggers & Safety (Specific sensitive question)
  'crisis.4': true,     // "Have you ever had thoughts of self-harm or suicide?"

  // Sexual Health & Safety (ALL questions in category)
  'sexual.1': true,     // STI testing expectations
  'sexual.2': true,     // Consent communication preferences
  'sexual.3': true,     // Health conditions affecting sexuality
  'sexual.4': true,     // Absolute boundaries
  'sexual.5': true,     // Birth control responsibilities

  // Mental Health Specifics (ALL questions in category)
  'mental.1': true,     // Mental health diagnoses
  'mental.2': true,     // Thoughts on psychiatric medication
  'mental.3': true,     // Signs of mental health struggles
  'mental.4': true,     // How mental health affected previous relationships
  'mental.5': true,     // Current therapy/support

  // ========== JOINT QUESTIONS ==========

  // Substance Use Agreements (ALL questions in category)
  'j-substance.1': true,   // Alcohol consumption agreement
  'j-substance.2': true,   // Recreational drug use agreement
  'j-substance.3': true,   // Circumstances for seeking help
  'j-substance.4': true,   // Support if partner changes substance relationship
  'j-substance.5': true,   // Substance use around children/family

  // Crisis Protocols (ALL questions in category)
  'j-crisis.1': true,   // Mental health emergency plan
  'j-crisis.2': true,   // Suicide/self-harm protocol (emergency contacts)
  'j-crisis.3': true,   // Emergency contacts list
  'j-crisis.4': true,   // Financial crisis agreement
  'j-crisis.5': true,   // De-escalation protocol
  'j-crisis.6': true,   // External support involvement

  // Legal & Financial Infrastructure (ALL questions in category)
  'j-legal.1': true,    // Legal marriage/partnership timeline (medical POA names)
  'j-legal.2': true,    // Financial merging/separation agreement
  'j-legal.3': true,    // Estate planning documents timeline
  'j-legal.4': true,    // Healthcare proxies and medical decision-making
  'j-legal.5': true,    // Beneficiary designations
  'j-legal.6': true,    // Prenuptial/cohabitation agreements

  // Sexual Health & Consent (ALL questions in category)
  'j-sexual.1': true,   // STI testing agreement
  'j-sexual.2': true,   // Birth control agreement
  'j-sexual.3': true,   // Consent protocol
  'j-sexual.4': true,   // Shared boundaries
  'j-sexual.5': true,   // Mismatched desire handling
  'j-sexual.6': true,   // Sharing intimate details agreement

  // Deal Breakers & Exit Protocols (Specific sensitive question)
  'j-exit.3': true,     // "If we decided to separate, how would we handle shared assets and responsibilities?"
};

/**
 * Checks if a question response should be encrypted
 *
 * Per privacy-preserving-analytics-spec.md, ALL question responses are encrypted
 * when encryption is enabled (shouldEncrypt() returns true).
 *
 * @param questionId - Question identifier (unused, kept for API compatibility)
 * @returns true - all fields are considered PII and should be encrypted
 */
export function isPIIField(questionId: string): boolean {
  // Per Data Classification spec: ALL question responses are encrypted
  return true;
}

/**
 * DEPRECATED: All categories are now encrypted when shouldEncrypt() is true.
 * This list is kept for backwards compatibility only.
 *
 * Per privacy-preserving-analytics-spec.md, ALL question responses are encrypted.
 */
export const FULLY_ENCRYPTED_CATEGORIES = [
  // Individual categories
  'Substance Use & Addictive Behaviors',
  'Sexual Health & Safety',
  'Mental Health Specifics',

  // Joint categories
  'Substance Use Agreements',
  'Crisis Protocols',
  'Legal & Financial Infrastructure',
  'Sexual Health & Consent',
];

/**
 * Checks if a category has all questions encrypted
 *
 * Per privacy-preserving-analytics-spec.md, ALL categories are encrypted
 * when encryption is enabled.
 *
 * @param category - Category name (unused, kept for API compatibility)
 * @returns true - all categories are encrypted when shouldEncrypt() is true
 */
export function isCategoryFullyEncrypted(category: string): boolean {
  // Per Data Classification spec: ALL categories are encrypted
  return true;
}
